import React, { useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import { Button } from "@mui/material";
import NitroService from "../../../utils/nitro.service";
import RailStationSearchPanel from "./RailStationSearchPanel";
import {
  IDisplayStation,
  IRailStationSearchResult,
  IRailStationSearchFilters,
  ISCRSRecord,
  RailStationLookupPanelProps,
} from "./IRailStationLookup";
import "./RailStationLookupPanel.css";

const toTitleCase = (value: string) =>
  value
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const RailStationLookupPanel: React.FC<RailStationLookupPanelProps> = ({
  open,
  destinationName,
  defaultFilterLabel,
  selectedScrsId,
  stations,
  loading,
  onClose,
  onNext,
  onDisplayStationsEmptyChange,
}) => {
  const [selectedStationKey, setSelectedStationKey] = useState<string>("");
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [searchFilters, setSearchFilters] = useState<IRailStationSearchFilters>(
    {
      cityState: defaultFilterLabel,
      stationState: "",
      customerName: "",
      address: "",
      zipCode: "",
    },
  );
  const [appliedFilters, setAppliedFilters] =
    useState<IRailStationSearchFilters>({
      cityState: "",
      stationState: "",
      customerName: "",
      address: "",
      zipCode: "",
    });
  const [scrsResults, setScrsResults] = useState<ISCRSRecord[]>([]);
  const [isScrsLoading, setIsScrsLoading] = useState(false);
  const [showNoSCRSEmptyState, setShowNoSCRSEmptyState] = useState(false);
  const [radiusStations, setRadiusStations] = useState<
    IRailStationSearchResult[]
  >([]);
  const [isRadiusLoading, setIsRadiusLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedStationKey("");
      setShowSearchPanel(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const loadInitialSCRS = async () => {
      const initialCityState = defaultFilterLabel;
      const initialParams = {
        cityState: initialCityState,
        stationState: "",
        customerName: "",
        address: "",
        zipCode: "",
      };

      setSearchFilters(initialParams);
      setAppliedFilters({
        cityState: initialCityState,
        stationState: "",
        customerName: "",
        address: "",
        zipCode: "",
      });

      setIsScrsLoading(true);
      try {
        const initialRecords =
          (await NitroService.getSCRSRailData(initialParams)) || [];

        setScrsResults(initialRecords as ISCRSRecord[]);
        setSelectedStationKey("");
        setRadiusStations([]);
        setShowNoSCRSEmptyState(initialRecords.length === 0);
      } catch (error) {
        console.error("Error fetching SCRS reference data:", error);
        setScrsResults([]);
        setShowNoSCRSEmptyState(true);
      } finally {
        setIsScrsLoading(false);
      }
    };

    loadInitialSCRS();
  }, [defaultFilterLabel, open]);

  useEffect(() => {
    if (!open) {
      setScrsResults([]);
      setSearchFilters({
        cityState: defaultFilterLabel,
        stationState: "",
        customerName: "",
        address: "",
        zipCode: "",
      });
      setAppliedFilters({
        cityState: "",
        stationState: "",
        customerName: "",
        address: "",
        zipCode: "",
      });
      setShowNoSCRSEmptyState(false);
      setRadiusStations([]);
    }
  }, [defaultFilterLabel, open]);

  const fetchSCRS = async (params: IRailStationSearchFilters) => {
    setIsScrsLoading(true);
    try {
      const records = await NitroService.getSCRSRailData(params);
      setScrsResults(records as ISCRSRecord[]);
      setSelectedStationKey("");
      setRadiusStations([]);
      return records as ISCRSRecord[];
    } catch (error) {
      console.error("Error fetching SCRS reference data:", error);
      setScrsResults([]);
      return [];
    } finally {
      setIsScrsLoading(false);
    }
  };

  const displayStations = useMemo(() => {
    if (radiusStations.length > 0) {
      return radiusStations.map((station) => {
        const carriersText = station.carriers.join(", ");

        return {
          name: station.name,
          value: station.id,
          title: station.name,
          carriers: station.carriers,
          carriersLine: carriersText,
          locationCity: station.city || "",
          locationState: station.state || "",
          locationZip: Array.isArray(station.zipcodes)
            ? station.zipcodes.join(", ")
            : station.zipcodes || "",
        } as IDisplayStation;
      });
    }

    if (scrsResults.length > 0) {
      return scrsResults.map((record, index) => {
        const scrs = record.scrs || {};
        const stationValue =
          scrs.stationState || `${scrs.customerName || "STATION"}-${index}`;
        const allCarriers = [
          ...(scrs.carrier ? [scrs.carrier] : []),
          ...(scrs.connectingCarriers || []),
        ];
        const carriersText = allCarriers.length
          ? allCarriers.join(", ")
          : "No connecting carriers";

        return {
          name: scrs.stationName || stationValue,
          value: stationValue,
          scrsId: scrs.id || null,
          carriers: allCarriers.filter(Boolean),
          stationState: scrs.stationState || "",
          title: scrs.customerName || stationValue,
          stationNameLine: scrs.stationName || "",
          addressLine: scrs.address || "",
          locationCity: scrs.city || "",
          locationState: scrs.stationStateCode || "",
          locationZip: scrs.zip || "",
          carriersLine: carriersText,
        } as IDisplayStation;
      });
    }

    return stations.map((station) => {
      const cleanedName = toTitleCase(station.name || station.value || "");
      const parts = cleanedName.split(" ");
      const state = parts.length > 1 ? parts[parts.length - 1] : "";
      const city =
        parts.length > 1 ? parts.slice(0, -1).join(" ") : cleanedName;

      return {
        name: station.name,
        value: station.value,
        title: city || cleanedName,
        subtitle: state ? `${city}, ${state}` : cleanedName,
      } as IDisplayStation;
    });
  }, [radiusStations, scrsResults, stations]);

  useEffect(() => {
    if (
      !open ||
      !selectedScrsId ||
      selectedStationKey ||
      radiusStations.length
    ) {
      return;
    }

    const matchIndex = scrsResults.findIndex(
      (record) => record?.scrs?.id === selectedScrsId,
    );

    if (matchIndex < 0 || matchIndex >= displayStations.length) {
      return;
    }

    const matchedStation = displayStations[matchIndex];
    setSelectedStationKey(`${matchedStation.value}-${matchIndex}`);
  }, [
    displayStations,
    open,
    radiusStations.length,
    scrsResults,
    selectedScrsId,
    selectedStationKey,
  ]);

  useEffect(() => {
    if (!open || showSearchPanel || loading || isScrsLoading) return;

    onDisplayStationsEmptyChange?.(displayStations.length === 0);
  }, [
    displayStations.length,
    isScrsLoading,
    loading,
    onDisplayStationsEmptyChange,
    open,
    showSearchPanel,
  ]);

  const isRadiusSearchFlow = radiusStations.length > 0;

  const selectedFilterValues = isRadiusSearchFlow
    ? [
        appliedFilters.customerName,
        appliedFilters.stationState,
        appliedFilters.address,
        appliedFilters.zipCode,
      ].filter(Boolean)
    : [
        appliedFilters.customerName,
        appliedFilters.cityState,
        appliedFilters.stationState,
        appliedFilters.address,
        appliedFilters.zipCode,
      ].filter(Boolean);

  const searchInputSummary = selectedFilterValues.join(" • ");

  const radiusFilterOptions = useMemo(() => {
    if (!isRadiusSearchFlow) {
      return {
        stationNames: [],
        cities: [],
        states: [],
        zipCodes: [],
      };
    }

    const unique = (values: string[]) =>
      Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" }),
      );

    return {
      stationNames: unique(radiusStations.map((entry) => entry.name || "")),
      cities: unique(radiusStations.map((entry) => entry.city || "")),
      states: unique(radiusStations.map((entry) => entry.state || "")),
      zipCodes: unique(radiusStations.map((entry) => entry.zipcode || "")),
    };
  }, [isRadiusSearchFlow, radiusStations]);

  if (!open) return null;

  const handleBackToEmptyState = () => {
    setSelectedStationKey("");
    setRadiusStations([]);
    setShowNoSCRSEmptyState(true);
  };

  const openFilterSearchPanel = () => {
    setShowSearchPanel(true);
  };

  const handleSearchSubmit = async (filters: IRailStationSearchFilters) => {
    const normalizedFilters: IRailStationSearchFilters = {
      cityState: (filters.cityState || "").trim(),
      stationState: (filters.stationState || "").trim(),
      customerName: (filters.customerName || "").trim(),
      address: (filters.address || "").trim(),
      zipCode: (filters.zipCode || "").trim(),
    };

    setSearchFilters(normalizedFilters);
    setAppliedFilters(normalizedFilters);

    if (!isRadiusSearchFlow) {
      const records = await fetchSCRS(normalizedFilters);
      setShowNoSCRSEmptyState(records.length === 0);
    }

    setShowSearchPanel(false);
  };

  const handleSearchClear = () => {
    const emptyFilters = {
      cityState: "",
      stationState: "",
      customerName: "",
      address: "",
      zipCode: "",
    };

    setSearchFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  const searchRailStationsInRadius = async () => {
    const cityStateValue = searchFilters.cityState;
    if (!cityStateValue) return;

    setIsRadiusLoading(true);
    try {
      const [city, state] = cityStateValue
        .split(",")
        .map((part) => part.trim());
      const cityStateRecords = await NitroService.getCityStates(
        city || "",
        state || "",
      );

      const matchingRecord = cityStateRecords.find(
        (record) =>
          (record?.name || "").toLowerCase() === cityStateValue.toLowerCase(),
      );

      const fallbackRecord = cityStateRecords[0];
      const zipCode =
        matchingRecord?.zipCodes?.[0] || fallbackRecord?.zipCodes?.[0];

      if (!zipCode) {
        setRadiusStations([]);
        return;
      }

      const records = await NitroService.getRailStationsByZipCode({ zipCode });
      const mappedStations = records.slice().sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      );

      setRadiusStations(mappedStations);
      setSearchFilters((prev) => ({
        ...prev,
        customerName: "",
        stationState: "",
        address: "",
        zipCode: "",
      }));
      setAppliedFilters((prev) => ({
        ...prev,
        customerName: "",
        stationState: "",
        address: "",
        zipCode: "",
      }));
      setShowNoSCRSEmptyState(false);
      setSelectedStationKey("");
    } catch (error) {
      console.error("Error searching rail stations in 50mi radius:", error);
      setRadiusStations([]);
    } finally {
      setIsRadiusLoading(false);
    }
  };

  if (showSearchPanel) {
    return (
      <RailStationSearchPanel
        open={open}
        filters={searchFilters}
        loading={isScrsLoading}
        isRadiusSearchFlow={isRadiusSearchFlow}
        radiusFilterOptions={radiusFilterOptions}
        onSearch={handleSearchSubmit}
        onClear={handleSearchClear}
        onBack={(filters) => {
          setSearchFilters(filters);
          setShowSearchPanel(false);
        }}
      />
    );
  }

  const selected =
    displayStations.find(
      (station, index) => `${station.value}-${index}` === selectedStationKey,
    ) || null;
  const filteredStations = displayStations
    .map((station, index) => ({
      station,
      stationItemKey: `${station.value}-${index}`,
    }))
    .filter(({ station }) => {
      if (!isRadiusSearchFlow) {
        return true;
      }

      const matchBy = (raw: string | undefined, selectedValue: string) =>
        !selectedValue ||
        (raw || "").toLowerCase() === selectedValue.toLowerCase();

      return (
        matchBy(station.title, appliedFilters.customerName) &&
        matchBy(station.locationCity, appliedFilters.stationState) &&
        matchBy(station.locationState, appliedFilters.address) &&
        matchBy(station.locationZip, appliedFilters.zipCode || "")
      );
    });
  let listContent: React.ReactNode = null;

  if (loading || isScrsLoading) {
    listContent = (
      <div className="rail-station-lookup-empty">Loading rail stations...</div>
    );
  } else if (showNoSCRSEmptyState || displayStations.length === 0) {
    listContent = (
      <div className="rail-station-lookup-empty-state">
        <ReportGmailerrorredIcon className="rail-station-lookup-empty-state-icon" />
        <div className="rail-station-lookup-empty-state-title">
          Looks Like We Came Up Empty
        </div>
        <p className="rail-station-lookup-empty-state-subtitle">
          Try adjusting your filters. If nothing appears, use the button below
          to search a broader list of stations.
        </p>
        <button
          id="rail-station-lookup-radius-search-btn"
          type="button"
          className="rail-station-lookup-empty-state-btn"
          onClick={searchRailStationsInRadius}
          disabled={isRadiusLoading}
        >
          {isRadiusLoading
            ? "Searching Rail Stations..."
            : "Search Rail Stations in a 50mi Radius"}
        </button>
      </div>
    );
  } else {
    listContent = filteredStations.map(({ station, stationItemKey }) => {
      const isSelected = selectedStationKey === stationItemKey;
      const isScrsStation = Boolean(station.scrsId);
      const cityState = [station.locationCity, station.locationState]
        .filter(Boolean)
        .join(", ");
      const addressCityStateZip = [
        station.addressLine,
        cityState,
        station.locationZip,
      ]
        .filter(Boolean)
        .join(", ");
      const scrsStationName = toTitleCase(station.name || "");
      const nonScrsStationName = (station.name || "").toUpperCase();
      let stationDetailsContent: React.ReactNode;

      if (isScrsStation) {
        stationDetailsContent = (
          <>
            {scrsStationName && (
              <div className="rail-station-lookup-item-scrs-station-name">
                {scrsStationName}
              </div>
            )}
            {addressCityStateZip && (
              <div className="rail-station-lookup-item-scrs-address-city-state-zip">
                {addressCityStateZip}
              </div>
            )}
            {station.carriersLine && (
              <div className="rail-station-lookup-item-scrs-carriers">
                {station.carriersLine}
              </div>
            )}
          </>
        );
      } else if (station.locationCity !== undefined) {
        stationDetailsContent = (
          <>
            <div className="rail-station-lookup-item-non-scrs-row">
              {nonScrsStationName && (
                <span className="rail-station-lookup-item-non-scrs-station-name">
                  {nonScrsStationName}
                </span>
              )}
              {nonScrsStationName && station.carriersLine && (
                <span className="rail-station-lookup-item-location-dot" />
              )}
              {station.carriersLine && (
                <span className="rail-station-lookup-item-non-scrs-carriers">
                  {station.carriersLine}
                </span>
              )}
            </div>
            <div className="rail-station-lookup-item-non-scrs-row">
              {station.locationCity && (
                <span className="rail-station-lookup-item-location-part">
                  {station.locationCity}
                </span>
              )}
              {station.locationState && (
                <>
                  <span className="rail-station-lookup-item-location-dot" />
                  <span className="rail-station-lookup-item-location-part">
                    {station.locationState}
                  </span>
                </>
              )}
              {station.locationZip && (
                <>
                  <span className="rail-station-lookup-item-location-dot" />
                  <span className="rail-station-lookup-item-location-part">
                    {station.locationZip}
                  </span>
                </>
              )}
            </div>
          </>
        );
      } else {
        stationDetailsContent = (
          <div
            id={`rail-station-lookup-item-subtitle-${stationItemKey}`}
            className="rail-station-lookup-item-subtitle"
          >
            {station.subtitleLines ? (
              <div className="rail-station-lookup-item-subtitle-column">
                {station.subtitleLines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </div>
            ) : (
              station.subtitle
            )}
          </div>
        );
      }

      return (
        <div
          key={stationItemKey}
          className={`rail-station-lookup-item ${isSelected ? "selected" : ""}`}
        >
          <div
            id={`rail-station-lookup-item-info-${stationItemKey}`}
            className="rail-station-lookup-item-info"
          >
            {isScrsStation && (
              <div
                id={`rail-station-lookup-item-title-${stationItemKey}`}
                className="rail-station-lookup-item-title"
              >
                {station.title}
              </div>
            )}
            {stationDetailsContent}
          </div>
          <button
            id={`rail-station-lookup-select-btn-${stationItemKey}`}
            type="button"
            className={`rail-station-lookup-select-btn ${
              isSelected ? "active" : ""
            }`}
            onClick={() => setSelectedStationKey(stationItemKey)}
          >
            Select
          </button>
        </div>
      );
    });

    if (filteredStations.length === 0) {
      listContent = (
        <div className="rail-station-lookup-empty">
          No stations match your search.
        </div>
      );
    }
  }

  return (
    <div className="rail-station-lookup-overlay">
      <div className="rail-station-lookup-panel">
        <div className="rail-station-lookup-header">
          {isRadiusSearchFlow && (
            <button
              type="button"
              className="rail-carrier-review-icon-btn rail-station-lookup-back"
              onClick={handleBackToEmptyState}
              aria-label="Back"
            >
              <ArrowBackIcon fontSize="small" />
            </button>
          )}
          <div className="rail-station-lookup-stepper" aria-hidden>
            <div className="rail-station-lookup-stepper-line" />
            <div className="rail-station-lookup-stepper-dot" />
          </div>
          <button
            type="button"
            aria-label="Close rail station lookup"
            className="rail-station-lookup-close"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </button>
          <div className="rail-station-lookup-kicker">
            {`ADDING RAIL STATION TO ${destinationName}`}
          </div>
          <div className="rail-station-lookup-title">Select a Station</div>
          <div
            className="rail-station-lookup-search-wrap"
            role="button"
            tabIndex={0}
            onClick={openFilterSearchPanel}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openFilterSearchPanel();
              }
            }}
          >
            <FilterAltOutlinedIcon className="rail-station-lookup-search-icon" />
            <input
              id="rail-station-lookup-search-input"
              type="text"
              className="rail-station-lookup-search-input"
              placeholder="Search by Station Name, City, State, or Zip Code"
              value={searchInputSummary}
              readOnly
              onClick={openFilterSearchPanel}
            />
          </div>
        </div>

        <div id="rail-station-lookup-list" className="rail-station-lookup-list">
          {listContent}
        </div>

        <div className="rail-station-lookup-footer">
          <Button
            id="rail-station-lookup-next-btn-id"
            variant="contained"
            disabled={!selected || loading || isScrsLoading}
            className="rail-station-lookup-next"
            onClick={() => {
              if (selected) {
                onNext({
                  name: selected.name,
                  value: selected.value,
                  scrsId: selected.scrsId,
                  carriers: selected.carriers,
                  stationState: selected.stationState,
                });
              }
            }}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RailStationLookupPanel;
